import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarX2, CheckCircle2, Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const call = async (url, opts) => {
  const r = await fetch(url, opts).catch(() => null);
  const j = r ? await r.json().catch(() => ({})) : {};
  if (!r?.ok) throw new Error(formatApiErrorDetail(j.detail?.message || j.detail) || "Something went wrong — please try again");
  return j;
};

export default function AppCancelBooking() {
  const { appId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const base = `${BACKEND}/api/apps/${appId}/booking/cancel`;
  const [booking, setBooking] = useState(null);
  const [state, setState] = useState({ busy: false, error: "" });

  useEffect(() => {
    call(`${base}?token=${encodeURIComponent(token)}`).then(setBooking).catch((e) => setState({ busy: false, error: e.message }));
  }, [base, token]);

  const cancel = async () => {
    setState({ busy: true, error: "" });
    try {
      setBooking(await call(base, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) }));
      setState({ busy: false, error: "" });
    } catch (e) { setState({ busy: false, error: e.message }); }
  };

  const cancelled = booking?.status === "Cancelled";
  return (
    <main data-testid="cancel-booking-page" className="noise grid min-h-screen place-items-center bg-[#07090E] px-5">
      <div className="card-tech w-full max-w-md space-y-4 rounded-2xl p-6">
        <p className="font-mono-tech text-xs uppercase tracking-widest text-cyan-400">// booking</p>
        <h1 className="text-2xl font-bold text-white">{cancelled ? "Booking cancelled" : "Cancel your booking"}</h1>
        {booking && (
          <dl data-testid="cancel-booking-details" className="space-y-1 text-sm text-slate-300">
            <div><dt className="inline text-slate-500">With </dt><dd className="inline text-white">{booking.app}</dd></div>
            <div><dt className="inline text-slate-500">Service </dt><dd className="inline text-white">{booking.service_name}</dd></div>
            <div><dt className="inline text-slate-500">When </dt><dd data-testid="cancel-booking-when" className="inline text-white">{booking.start_label}</dd></div>
            <div><dt className="inline text-slate-500">Timezone </dt><dd className="inline">{booking.timezone}</dd></div>
          </dl>
        )}
        {cancelled && <p data-testid="cancel-booking-done" className="flex items-start gap-2 text-sm text-emerald-300"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> This booking is cancelled and the time is free again.</p>}
        {state.error && <p data-testid="cancel-booking-error" className="text-sm text-red-400">{state.error}</p>}
        {booking && !cancelled && (
          <button onClick={cancel} disabled={state.busy} data-testid="cancel-booking-confirm" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60">
            {state.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarX2 className="h-4 w-4" />} Yes, cancel this booking
          </button>
        )}
      </div>
    </main>
  );
}
